import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import ReusableTable, {
  Column,
} from "../../../components/reusable-table/ReusableTable";
import Button from "../../../components/reusable-button/Button";
import {
  AdminUser,
  getAdminList,
  createAdmin,
  updateAdmin,
  resetAdminPassword,
} from "../../../apis/adminApis";
import AdminModal from "./admin-modal/AdminModal";
import ResetPasswordModal from "./admin-modal/ResetPasswordModal";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { showSnackbar } from "../../../redux/slices/snackbarSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store/store";
import { setAdmins } from "../../../redux/slices/adminSlice";
import { fetchCmsRolesList } from "../../../apis/adminApis";
import { CmsRole } from "../../../apis/adminApis";
import { setRoles } from "../../../redux/slices/roleSlice";
import ReusableLoader from "../../../components/reusable-loader/ReusableLoader";
import { formatName } from "../../../utils/formatName";
import { TOAST_MESSAGES } from "../../../constants/toastMessages";
import "./ManageAdmin.css";

const ManageAdmin: React.FC = () => {
  const dispatch = useDispatch();
  const adminState = useSelector((state: RootState) => state.admin);
  const roleState = useSelector((state: RootState) => state.role);

  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);
  const [tab, setTab] = useState<"active" | "inactive">("active");
  const [isLoading, setIsLoading] = useState(false);

  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [menuRow, setMenuRow] = useState<AdminUser | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminUser | null>(null);

  const MoreIcon = BiDotsVerticalRounded as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const result = await getAdminList();
      if (result.success && result.data) {
        dispatch(setAdmins(result.data));
      }
    } catch (err) {
      console.error("Failed to fetch admin list:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminState.needRefresh) {
      fetchAdmins();
    } else {
      setLoading(false);
    }
  }, [adminState.needRefresh]);

  const fetchRoles = async () => {
    try {
      const res = await fetchCmsRolesList();
      if (res.success && res.data) {
        dispatch(setRoles(res.data));
      }
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    }
  };

  useEffect(() => {
    if (roleState.needRefresh) {
      fetchRoles();
    }
  }, [roleState.needRefresh]);

  const handleAddClick = () => {
    setEditingAdmin(null);
    setModalOpen(true);
  };

  const handleEditClick = (admin: AdminUser) => {
    setEditingAdmin(admin);
    setModalOpen(true);
    closeMenu();
  };

  const handleResetClick = (admin: AdminUser) => {
    setSelectedAdmin(admin);
    setResetModalOpen(true);
    closeMenu();
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingAdmin(null);
  };

  const handleModalSubmit = async (
    formData: Omit<AdminUser, "id" | "createdAt" | "updatedAt"> & {
      password?: string;
      roleId: string;
    }
  ) => {
    setIsLoading(true);
    try {
      if (editingAdmin) {
        const response = await updateAdmin(editingAdmin.id, formData);
        if (response?.success) {
          dispatch(
            showSnackbar({
              message: response.data || TOAST_MESSAGES.ADMIN.UPDATE_SUCCESS,
              type: "success",
            })
          );
        }
      } else {
        const response = await createAdmin({
          ...formData,
          password: formData.password!,
        });

        if (response?.success) {
          dispatch(
            showSnackbar({
              message: TOAST_MESSAGES.ADMIN.CREATE_SUCCESS,
              type: "success",
            })
          );
        }
      }

      await fetchAdmins();
      handleModalClose();
    } catch (error) {
      const message =
        (error as Error)?.message || TOAST_MESSAGES.ADMIN.ADMIN_ERROR;
      dispatch(showSnackbar({ message, type: "error" }));
    } finally {
      setIsLoading(false);
    }
  };

  const openMenu = (e: React.MouseEvent, id: string, row: AdminUser) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const menuWidth = 180;
    const gap = 8;
    let left = rect.right - menuWidth + window.scrollX;
    left = Math.max(
      8 + window.scrollX,
      Math.min(left, window.innerWidth - menuWidth - 8 + window.scrollX)
    );
    const top = rect.bottom + gap + window.scrollY;

    setMenuPos({ top, left });
    setMenuOpenId(id);
    setMenuRow(row);
  };

  const closeMenu = () => {
    setMenuOpenId(null);
    setMenuRow(null);
  };

  const handleResetPasswordSubmit = async (password: string) => {
    if (!selectedAdmin) return;

    setIsLoading(true);
    try {
      const res = await resetAdminPassword(selectedAdmin.id, { password });

      if (res.success) {
        dispatch(
          showSnackbar({
            message: res?.data || "Password reset successfully",
            type: "success",
          })
        );
        setResetModalOpen(false);
        setSelectedAdmin(null);
      } else {
        dispatch(
          showSnackbar({
            message: res.message || "Failed to reset password",
            type: "error",
          })
        );
      }
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || "Failed to reset password",
          type: "error",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!menuOpenId) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") closeMenu();
    };
    const onResize = () => closeMenu();
    const onScroll = () => closeMenu();

    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [menuOpenId]);

  const columns: Column<AdminUser>[] = [
    {
      header: "Name",
      render: (row) =>
        `${row.firstName} ${row.middleName} ${row.lastName}`
          .replace(/\s+/g, " ")
          .trim(),
    },
    { header: "Email", accessor: "email" },
    {
      header: "Role",
      render: (row) =>
        typeof row.roleIds.roleName === "string" ? (
          <span style={{ whiteSpace: "nowrap" }}>
            {formatName(row.roleIds.roleName)}
          </span>
        ) : (
          ""
        ),
    },
    {
      header: "Designation",
      render: (row) =>
        typeof row.designation === "string" ? row.designation : "",
    },
    {
      header: "Actions",
      render: (row) => (
        <div className="admin-action-cell">
          <MoreIcon
            onClick={(e) =>
              menuOpenId === row.id ? closeMenu() : openMenu(e, row.id, row)
            }
            className="admin-menu-icon"
          />
        </div>
      ),
    },
  ];

  const filteredAdmins = adminState.admins.filter(
    (admin) => admin.isActive === (tab === "active")
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2>Manage Admins ({filteredAdmins.length})</h2>
        <div>
          <Button label="+ Add Admin" onClick={handleAddClick} />
        </div>
      </div>

      <div style={{ display: "flex" }}>
        <button
          onClick={() => setTab("active")}
          style={{
            padding: "8px 16px",
            backgroundColor: tab === "active" ? "#28606B" : "#f0f0f0",
            color: tab === "active" ? "#fff" : "#000",
            border: "none",
            borderTopLeftRadius: "6px",
            borderBottomLeftRadius: "6px",
            cursor: "pointer",
          }}
        >
          Active
        </button>
        <button
          onClick={() => setTab("inactive")}
          style={{
            padding: "8px 16px",
            backgroundColor: tab === "inactive" ? "#28606B" : "#f0f0f0",
            color: tab === "inactive" ? "#fff" : "#000",
            border: "none",
            borderTopRightRadius: "6px",
            borderBottomRightRadius: "6px",
            cursor: "pointer",
          }}
        >
          Inactive
        </button>
      </div>

      {loading ? (
        <ReusableLoader />
      ) : (
        <ReusableTable columns={columns} rows={filteredAdmins} />
      )}

      {modalOpen && (
        <AdminModal
          open={modalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          initialData={editingAdmin}
          roles={roleState.roles}
          loading={isLoading}
        />
      )}

      {resetModalOpen && selectedAdmin && (
        <ResetPasswordModal
          open={resetModalOpen}
          onClose={() => setResetModalOpen(false)}
          admin={selectedAdmin}
          onSubmit={handleResetPasswordSubmit}
        />
      )}

      {menuOpenId &&
        createPortal(
          <>
            <div className="admin-menu-backdrop" onClick={closeMenu} />
            <div
              className="admin-menu-portal"
              style={{ top: `${menuPos.top}px`, left: `${menuPos.left}px` }}
              role="menu"
            >
              <button
                className="admin-menu-item"
                onClick={() => menuRow && handleEditClick(menuRow)}
              >
                Edit
              </button>
              <button
                className="admin-menu-item"
                onClick={() => menuRow && handleResetClick(menuRow)}
              >
                Reset Password
              </button>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default ManageAdmin;
